import { createSelector } from 'reselect';
import isUndefined from 'lodash/isUndefined';
import get from 'lodash/get';
import { selectFeatureFlags } from 'yoda-site-components/lib/selectors/ContextSelector';
import {
    initProductOptions,
    getCongruentSelected,
} from 'yoda-site-components/lib/selectors/ProductOptionsSelector';
import { isDeliveryOptionRequired } from 'yoda-site-components/lib/selectors/ProductDetailsSelector';
import {
    selectDeliveryDate,
    selectDeliveryZipcode,
} from 'yoda-site-components/lib/selectors/fromState';
import {
    selectClearance,
    selectCloseout,
} from 'yoda-site-components/lib/selectors/ProductPricingSelector';
import deliveryOptions from './delivery/deliverOptionsSelector';
import selectSkuId from './selectedByUser/selectSkuId';

const getQueryParams = (state) => state.queryParams;
const getProductInStock = (state) => initProductOptions(false)(state).isProductInStock;
const getItemInStock = (state) => initProductOptions(false)(state).isItemInStock;
const getDimensions = (state) => initProductOptions(false)(state).dimensions;
const getCurrentFilters = (state) => initProductOptions(false)(state).optionsSelected;
const getFilteredItems = (state) => initProductOptions(false)(state).filteredItems;
const getPricingURL = (state) => initProductOptions(false)(state).pricingURL;
const getMyAlertIndicatorOnLoad = (state) => initProductOptions(false)(state).myAlertIndicator;

const getProductOfferingData = (state) => {
    let isSelectedItemInStock = '';
    let myAlertIndicatorOnskuSelection = '';
    if (state.productOffering && Object.keys(state.productOffering).length) {
        const { productOffering } = state;
        /* istanbul ignore next */
        if ('myAlertIndicator' in productOffering) {
            /** Checking the selected product whether instock or out of stock */
            const getSelectedProductData =
                Object.keys(deliveryOptions(state).shipToHomeOrStore).length > 0
                    ? deliveryOptions(state)
                    : state.deliveryOption;
            const { sameDayPickUp, shipToHomeOrStore } = Object.keys(getSelectedProductData).length
                ? getSelectedProductData
                : {};
            const sameDayPickupAvailable =
                sameDayPickUp && !isUndefined(sameDayPickUp.isSameDayPickupDisabled)
                    ? !sameDayPickUp.isSameDayPickupDisabled
                    : false;
            const shipToHomeOrStoreAvailable =
                shipToHomeOrStore && !isUndefined(shipToHomeOrStore.isshipToStoreDisabled)
                    ? !shipToHomeOrStore.isshipToStoreDisabled
                    : false;

            isSelectedItemInStock = sameDayPickupAvailable || shipToHomeOrStoreAvailable || false;
            myAlertIndicatorOnskuSelection = productOffering.myAlertIndicator;
        }
    }
    return {
        isSelectedItemInStock,
        myAlertIndicatorOnskuSelection,
    };
};

const selectAvailableForZipcode = createSelector(
    [selectSkuId, selectDeliveryDate, selectDeliveryZipcode],
    (skuId, deliveryDate, deliveryZipcode) => {
        /* istanbul ignore next */
        if (
            deliveryDate[skuId] &&
            deliveryDate[skuId][deliveryZipcode] &&
            'available' in deliveryDate[skuId][deliveryZipcode]
        ) {
            return !!(
                deliveryDate[skuId][deliveryZipcode].available &&
                deliveryDate[skuId][deliveryZipcode].earliestDeliveryDate
            );
        }
        return '';
    }
);

const getMyAlertIndicatorData = createSelector(
    [
        getMyAlertIndicatorOnLoad,
        getProductOfferingData,
        isDeliveryOptionRequired(false),
        selectAvailableForZipcode,
    ],
    (myAlertIndicatorOnLoad, productOfferingData, deliveryOptionRequired, availableForZipcode) => {
        const { myAlertIndicatorOnskuSelection } = productOfferingData;
        let { isSelectedItemInStock } = productOfferingData;
        let isOfferingCall = false;
        let myAlertIndicator = '';
        /* istanbul ignore next */
        if (typeof myAlertIndicatorOnskuSelection === 'boolean') {
            isOfferingCall = true;
            myAlertIndicator = false;
        } else if (typeof myAlertIndicatorOnLoad === 'boolean') {
            myAlertIndicator = false;
        }
        /* istanbul ignore next */
        if (deliveryOptionRequired) {
            isSelectedItemInStock = '';
            if (availableForZipcode !== '') {
                isSelectedItemInStock = availableForZipcode;
            }
        }

        return {
            myAlertIndicator,
            isSelectedItemInStock,
            isOfferingCall,
            deliveryOptionRequired,
        };
    }
);

const getMyAlerts = createSelector(
    [
        selectFeatureFlags,
        getQueryParams,
        getMyAlertIndicatorData,
        getProductInStock,
        getItemInStock,
        getDimensions,
        getCurrentFilters,
        getFilteredItems,
        getPricingURL,
        selectCloseout(false),
        selectClearance(false),
        getCongruentSelected,
    ],
    (
        featureFlags,
        query,
        { myAlertIndicator, isSelectedItemInStock, isOfferingCall, deliveryOptionRequired },
        productInStock,
        itemInStock,
        dimensions,
        currentFilters,
        filteredItems,
        pricingURL,
        isCloseout,
        isClearance,
        congruentSelected
    ) => {
        let buttonText = 'Add to Cart';
        let isGetInStock = false;
        let isOOSPro = false;
        const isAllSKUSelected = currentFilters.length === dimensions.length;
        const { myAlertsNew = true } = featureFlags;
        const isCongruent = get(congruentSelected, 'isCongruent', false);
        /* istanbul ignore next */
        if (typeof myAlertIndicator === 'boolean' && productInStock !== '') {
            if (productInStock) {
                if (isSelectedItemInStock !== '') {
                    if (myAlertIndicator) {
                        isGetInStock = true;
                    } else if (
                        (!isSelectedItemInStock &&
                            (isClearance || isCloseout || deliveryOptionRequired)) ||
                        !isSelectedItemInStock
                    ) {
                        buttonText = 'Out of Stock';
                        isOOSPro = true;
                    }
                }
                if (
                    get(featureFlags, 'enableSKUAvailabilityLogic', true) &&
                    !myAlertIndicator &&
                    !itemInStock &&
                    !isCongruent &&
                    !pricingURL.itemOfferingCall &&
                    ((filteredItems && filteredItems.length === 0) || isAllSKUSelected)
                ) {
                    buttonText = 'Not Available';
                    isOOSPro = true;
                }
                if (
                    get(featureFlags, 'enableProductSelection', false) &&
                    !myAlertIndicator &&
                    !isCongruent &&
                    !pricingURL.itemOfferingCall &&
                    ((filteredItems && filteredItems.length === 0) || isAllSKUSelected)
                ) {
                    buttonText = 'Not Available';
                    isOOSPro = true;
                }
            } else {
                if (myAlertIndicator) {
                    isGetInStock = true;
                }
                if (
                    (!myAlertIndicator &&
                        (isCloseout ||
                            isClearance ||
                            deliveryOptionRequired ||
                            (isSelectedItemInStock !== '' && !isSelectedItemInStock))) ||
                    !myAlertIndicator
                ) {
                    buttonText = 'Out of Stock';
                    isOOSPro = true;
                }
                if (
                    get(featureFlags, 'enableProductSelection', false) &&
                    !myAlertIndicator &&
                    !isCongruent &&
                    !pricingURL.itemOfferingCall &&
                    isSelectedItemInStock === '' &&
                    ((filteredItems && filteredItems.length === 0) || isAllSKUSelected)
                ) {
                    buttonText = 'Not Available';
                    isOOSPro = true;
                }
            }
        }
        if (query && query.cartId) {
            buttonText = 'Update Cart';
        }

        return {
            buttonText,
            isGetInStock,
            isOOSPro,
            isOfferingCall,
            isSelectedItemInStock,
            deliveryOptionRequired,
        };
    }
);

export default getMyAlerts;
